import { useSearchParams } from 'react-router-dom'
import type { ConnectionsScenario, ISODate } from '@app/shared'

import { isDemoMode } from '../../lib/data'
import type { DayScenario, NotificationRecord } from '../../lib/data/types'
import { SEED_TODAY } from '../../lib/data/demo'

// ============================================================================
// THE DAY SCREEN'S DEMO STATES — `/?state=<one>`, demo mode only (remote ignores it), the same
// mechanism the desktop Day page (`/day?state=`) and the Connections screen already use, so every
// honest state on boards 01, 22 and 26 can be opened and photographed without an account.
//
//   recording     the status chip as the live-recording bubble
//   notification  the chip as the notification state, and a populated notifications sheet
//   stale         a connector in error (Connections scenario 12a) and a Gmail account that
//                 stopped answering (the Day page's `degraded` mail)
//   offline       the browser says it is offline
//   quiet         nothing needs you, nothing due today
//   first-run     no Gmail account connected
//   loading       every card a skeleton
//
// THE DEFAULT DEMO SCREEN NEEDS NONE OF THIS, and its notification log stays empty: the demo has
// no push service, and the settings screen says so in as many words. The fixture rows below exist
// only inside the `notification` state.
// ============================================================================

export const MOBILE_DAY_SCENARIOS = [
  'recording',
  'notification',
  'stale',
  'offline',
  'quiet',
  'first-run',
  'loading',
] as const

export type MobileDayScenario = (typeof MOBILE_DAY_SCENARIOS)[number]

export function parseDayScenario(raw: string | null, demo: boolean): MobileDayScenario | null {
  if (!demo || raw === null) return null
  return (MOBILE_DAY_SCENARIOS as readonly string[]).includes(raw) ? (raw as MobileDayScenario) : null
}

export function useMobileDayScenario(): MobileDayScenario | null {
  const [params] = useSearchParams()
  return parseDayScenario(params.get('state'), isDemoMode)
}

/** What each state asks the data layer for. `null` everywhere is the plain demo screen. */
export interface ScenarioInputs {
  connectors: ConnectionsScenario | undefined
  triage: DayScenario | undefined
  /** Today's reminders replaced by none (quiet). */
  noReminders: boolean
  forceOffline: boolean
  forceLoading: boolean
  notifications: NotificationRecord[] | null
  recording: boolean
}

export function scenarioInputs(s: MobileDayScenario | null): ScenarioInputs {
  return {
    connectors: s === 'stale' ? 'connector_broken' : undefined,
    triage: s === 'stale' ? 'degraded' : s === 'quiet' ? 'quiet' : s === 'first-run' ? 'first-run' : s === 'loading' ? 'loading' : undefined,
    noReminders: s === 'quiet',
    forceOffline: s === 'offline',
    forceLoading: s === 'loading',
    notifications: s === 'notification' ? demoNotifications(SEED_TODAY) : null,
    recording: s === 'recording',
  }
}

const at = (date: ISODate, minutes: number) =>
  `${date}T${String(Math.floor(minutes / 60)).padStart(2, '0')}:${String(minutes % 60).padStart(2, '0')}:00-07:00`

/** Four fired rows of the kinds the log really has, on the seeded morning. */
export function demoNotifications(today: ISODate): NotificationRecord[] {
  const row = (
    id: string,
    kind: NotificationRecord['kind'],
    minutes: number,
    title: string,
    body: string,
    url: string,
  ): NotificationRecord => ({
    id,
    kind,
    sourceId: id,
    fireAt: at(today, minutes),
    title,
    body,
    url,
    dedupeKey: id,
    status: 'sent',
    sentAt: at(today, minutes),
    error: null,
    attempts: 1,
    createdAt: at(today, minutes),
  })
  return [
    row('notif-demo-day-1', 'class_start', 8 * 60 + 30, 'COGSCI 1 starts in 10 min', 'Lecture · tap to record', '/record?course=COGSCI%201'),
    row('notif-demo-day-2', 'task_reminder', 8 * 60 + 15, 'Email advisor about spring units', 'Reminder', '/tasks'),
    row('notif-demo-day-3', 'calendar_block', 7 * 60 + 50, 'Gym in 10 min', 'Calendar block · RSF', `/calendar?date=${today}`),
    row('notif-demo-day-4', 'gcal_event', 7 * 60 + 30, 'Dinner — Maya at 19:00', 'Google Calendar', `/calendar?date=${today}`),
  ]
}
